import { useEffect } from 'react'
import { useShowSingleEntity } from '@sketch-hq/sketch-web-renderer'

import { useCanvasScope } from '../scope'

/**
 * Isolates a focused frame in the renderer.
 *
 * Being the scene root is enough to isolate a Section, and keeps its children
 * navigable, so only a frame goes through the renderer's single-entity state.
 * That also fits the camera to the frame, which is what moves the view between
 * entities without remounting the canvas - the stored camera position is read
 * only at init (see useInitialCameraPosition).
 */
export function useIsolateFocusedFrame() {
  const scope = useCanvasScope()
  const [isolatedEntityUUID, setShowSingleEntity] = useShowSingleEntity()

  const focusedFrameUUID =
    scope?.focus.kind === 'frame' ? scope.focus.uuid : null

  /**
   * Only when the renderer isn't already showing what this view wants. Setting
   * it fits the camera, so re-asserting a state it is already in is not free:
   * the page canvas would fit the document over the position the canvas had
   * just been initialized at - the one a "Copy Link from Position" link carries
   * - because the setter's identity changes once the file's entities resolve,
   * which is after the camera has been placed (WEB-3968).
   */
  useEffect(() => {
    if (isolatedEntityUUID === focusedFrameUUID) {
      return
    }

    setShowSingleEntity(focusedFrameUUID)
  }, [isolatedEntityUUID, focusedFrameUUID, setShowSingleEntity])
}
